import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { useInboxToken } from "views/inbox/api/useInboxToken";
import {
  useAllPhoneNumbers,
  usePropertyPhoneDefaults
} from "views/phone-numbers/lib/hooks";
import { mailboxesApi } from "views/settings-communication/api/api";
import {
  resolvePropertyEmail,
  resolvePropertyPhone
} from "../lib/communicationDefaults";

const SYSTEM_DEFAULT_EMAIL = import.meta.env.VITE_APP_DEFAULT_EMAIL as
  | string
  | undefined;

type PropertyCommunicationDefault = {
  phone: string | null;
  email: string | null;
};

export const usePropertyCommunicationDefaults = ({
  propertyIds,
  enabled
}: {
  propertyIds: number[];
  enabled: boolean;
}) => {
  const { type } = useAuthContext();
  const { accessToken } = useInboxToken({ enabled });
  const hasToken = enabled && !!accessToken;
  const canReadEmail =
    hasToken && type.isCommunication && type.isCommunicationEmail;

  const { data: phoneDefaults } = usePropertyPhoneDefaults(hasToken);
  const { data: partnerNumbers } = useAllPhoneNumbers({ enabled: hasToken });

  const { data: emailDefaults } = useQuery({
    queryKey: ["PROPERTY_DEFAULT_EMAIL", "LIST", propertyIds],
    queryFn: () => mailboxesApi.listPropertyDefaults(propertyIds),
    enabled: canReadEmail && propertyIds.length > 0,
    retry: false
  });

  const { data: poolSystemEmail } = useQuery({
    queryKey: ["PROPERTY_DEFAULT_EMAIL", "SYSTEM_DEFAULT"],
    queryFn: mailboxesApi.getSystemDefaultEmail,
    enabled: canReadEmail && !SYSTEM_DEFAULT_EMAIL,
    retry: false
  });
  const systemEmail = SYSTEM_DEFAULT_EMAIL || poolSystemEmail || undefined;

  return useMemo(() => {
    const byPropertyId: Record<number, PropertyCommunicationDefault> = {};
    propertyIds.forEach((propertyId) => {
      byPropertyId[propertyId] = {
        phone:
          phoneDefaults && partnerNumbers
            ? resolvePropertyPhone({
                propertyId,
                savedDefault: phoneDefaults.find(
                  (row) => row.property_id === propertyId
                ),
                partnerNumbers: partnerNumbers.items
              })
            : null,
        email: emailDefaults
          ? resolvePropertyEmail({
              savedDefault: emailDefaults.find(
                (row) => row.property_id === propertyId
              ),
              systemEmail
            })
          : null
      };
    });
    return byPropertyId;
  }, [propertyIds, phoneDefaults, partnerNumbers, emailDefaults, systemEmail]);
};
